import {
  ArrowLeftRight,
  Check,
  ChevronDown,
  Coins,
  Download,
  FileImage,
  ImagePlus,
  LoaderCircle,
  RotateCcw,
  Sparkles,
  UploadCloud,
  X
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { estimateCredits, type ToolDefinition } from '../data/tools';
import { useAuth } from '../hooks/useAuth';
import { processImage, type ProcessResult } from '../lib/api';
import { cn, formatBytes, sleep } from '../lib/utils';
import { BeforeAfter } from './BeforeAfter';
import { SelectMenu } from './SelectMenu';
import { PLAN_ACCESS, effectivePlan, type AccountPlan } from '../../shared/billing';

type Stage = 'empty' | 'ready' | 'processing' | 'complete' | 'error';

const MIME_BY_FORMAT: Record<string, string> = {
  JPG: 'image/jpeg',
  PNG: 'image/png',
  WebP: 'image/webp',
  AVIF: 'image/avif'
};


async function waitForResultImage(url: string) {
  await new Promise<void>((resolve, reject) => {
    const image = new Image();
    const timeout = window.setTimeout(() => reject(new Error('The processed image took longer than expected to load. Please try again in a moment.')), 45_000);
    image.onload = () => {
      window.clearTimeout(timeout);
      resolve();
    };
    image.onerror = () => {
      window.clearTimeout(timeout);
      reject(new Error('The AI finished, but the saved result could not be loaded. Restart the local server and try again.'));
    };
    image.src = `${url}${url.includes('?') ? '&' : '?'}v=${Date.now()}`;
  });
}

function processingDurationText(toolSlug: ToolDefinition['slug'], options: Record<string, string | number | boolean>) {
  const scale = Number(options.scale ?? (toolSlug === 'enhance' ? 1 : 2));
  const detailStrength = Number(options.detailStrength ?? 70);
  if (toolSlug === 'enhance' && (scale >= 8 || detailStrength >= 85)) return 'Usually 1 to 4 minutes depending on image size and selected detail intensity.';
  if (toolSlug === 'upscale' && scale >= 8) return 'Usually 1 to 4 minutes depending on image size and selected upscale size.';
  if (toolSlug === 'restore' || toolSlug === 'product-photo' || toolSlug === 'relight') return 'Usually 1 to 3 minutes depending on image size and workload.';
  return 'Usually 30 seconds to 2 minutes depending on image size and server load.';
}

export function ImageWorkspace({ tool, compact = false }: { tool: ToolDefinition; compact?: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>('empty');
  const [dragging, setDragging] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [result, setResult] = useState<ProcessResult | null>(null);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('Preparing image');
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState('');
  const [megapixels, setMegapixels] = useState(1);
  const [imageSize, setImageSize] = useState<{ width: number; height: number } | null>(null);
  const { session, profile, refreshProfile, setCreditsRemaining } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const ToolIcon = tool.icon;
  const acceptedMimeTypes = tool.formats
    .map((format) => MIME_BY_FORMAT[format])
    .filter((value): value is string => Boolean(value));

  const initialOptions = useMemo(
    () => Object.fromEntries(tool.options.map((option) => [option.id, option.defaultValue])),
    [tool]
  );
  const [options, setOptions] = useState<Record<string, string | number | boolean>>(initialOptions);
  const estimatedCredits = estimateCredits(tool.slug, options, megapixels);
  const hasScaleOption = tool.options.some((option) => option.id === 'scale');
  const resolvedPlan = effectivePlan(profile?.plan, profile?.subscriptionStatus);
  const planAccess = PLAN_ACCESS[resolvedPlan];

  useEffect(() => {
    setOptions(initialOptions);
    reset();
  }, [tool.slug, initialOptions]);


  useEffect(() => {
    setOptions((current) => {
      const next = { ...current };
      let changed = false;
      if (Number(next.scale ?? 2) > planAccess.maxScale) {
        next.scale = String(planAccess.maxScale);
        changed = true;
      }
      return changed ? next : current;
    });
  }, [planAccess.maxScale]);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);



  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      const image = Array.from(event.clipboardData?.files ?? []).find((item) => item.type.startsWith('image/'));
      if (image) void acceptFile(image);
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []);

  async function acceptFile(nextFile: File) {
    setError('');
    if (!nextFile.type.startsWith('image/')) {
      setError('Choose a JPG, PNG, WebP or AVIF image.');
      setStage('error');
      return;
    }
    if (nextFile.size > 50 * 1024 * 1024) {
      setError('The image is larger than the 50 MB upload limit.');
      setStage('error');
      return;
    }
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    const objectUrl = URL.createObjectURL(nextFile);
    previewUrlRef.current = objectUrl;
    setFile(nextFile);
    setPreviewUrl(objectUrl);
    try {
      const bitmap = await createImageBitmap(nextFile);
      setMegapixels((bitmap.width * bitmap.height) / 1_000_000);
      setImageSize({ width: bitmap.width, height: bitmap.height });
      bitmap.close();
    } catch {
      setMegapixels(1);
      setImageSize(null);
    }
    setResult(null);
    setStage('ready');
  }

  function reset() {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = null;
    setFile(null);
    setPreviewUrl(null);
    setResult(null);
    setError('');
    setProgress(0);
    setDownloadError('');
    setDownloading(false);
    setMegapixels(1);
    setImageSize(null);
    setStage('empty');
  }

  async function run() {
    if (!file) return;
    if (!session) {
      const next = `${location.pathname}${location.search}${location.hash}` || `/tools/${tool.slug}`;
      navigate(`/auth?mode=signup&next=${encodeURIComponent(next)}`);
      return;
    }
    if (profile && profile.credits < estimatedCredits) {
      setError(`This job needs ${estimatedCredits} credits based on its size and settings. Upgrade or add more credits to continue.`);
      setStage('error');
      return;
    }

    setStage('processing');
    setError('');
    setProgress(8);
    setProgressLabel('Validating image');

    const progressSequence = async () => {
      await sleep(600);
      setProgress(22);
      setProgressLabel('Uploading your image securely');
      await sleep(900);
      setProgress(43);
      setProgressLabel(tool.slug === 'upscale' ? 'Expanding resolution intelligently' : 'Rebuilding clarity and useful detail');
      await sleep(1400);
      setProgress(67);
      setProgressLabel('Balancing texture, edges and natural tone');
      await sleep(1500);
      setProgress(84);
      setProgressLabel('Preparing the final result preview');
    };

    const request = processImage({
      file,
      tool: tool.slug,
      options,
      accessToken: session.access_token
    });

    try {
      void progressSequence();
      const output = await request;
      setProgress(94);
      setProgressLabel('Loading the completed image');
      await waitForResultImage(output.outputUrl);
      setProgress(100);
      setProgressLabel('Complete');
      await sleep(250);
      setResult(output);
      if (typeof output.creditsRemaining === 'number') setCreditsRemaining(output.creditsRemaining);
      setStage('complete');
      await refreshProfile();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Image processing failed.');
      setStage('error');
    }
  }

  async function downloadResult() {
    if (!result || downloading) return;
    setDownloading(true);
    setDownloadError('');
    try {
      const response = await fetch(result.downloadUrl, { method: 'HEAD', cache: 'no-store' });
      if (!response.ok) throw new Error('The result file is no longer available. Process the image again.');
      const link = document.createElement('a');
      link.href = result.downloadUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (caught) {
      setDownloadError(caught instanceof Error ? caught.message : 'The result could not be downloaded.');
    } finally {
      setDownloading(false);
    }
  }


  const detailStrength = tool.slug === 'enhance' ? Math.round(Number(options.detailStrength ?? 70)) : null;
  const selectedScaleLabel = tool.options.find((option) => option.id === 'scale')?.choices?.find((choice) => choice.value === String(options.scale ?? (tool.slug === 'enhance' ? '1' : '2')))?.label;
  const outputExtension = result?.downloadUrl ? result.downloadUrl.split('.').pop()?.split('?')[0]?.toUpperCase() : null;
  const processingHint = processingDurationText(tool.slug, options);
  const compareAspectRatio = result?.width && result?.height
    ? result.width / result.height
    : imageSize
      ? imageSize.width / imageSize.height
      : undefined;

  const dropHandlers = {
    onDragOver: (event: React.DragEvent) => {
      event.preventDefault();
      setDragging(true);
    },
    onDragLeave: () => setDragging(false),
    onDrop: (event: React.DragEvent) => {
      event.preventDefault();
      setDragging(false);
      const dropped = event.dataTransfer.files[0];
      if (dropped) void acceptFile(dropped);
    }
  };

  if (!session) {
    const next = `${location.pathname}${location.search}${location.hash}` || `/tools/${tool.slug}`;
    return (
      <section className={cn('workspace', 'workspace-auth-locked', compact && 'workspace-compact')}>
        <div className="workspace-toolbar">
          <div className="workspace-title">
            <span className="tool-icon"><ToolIcon size={20} /></span>
            <div><strong>{tool.name}</strong><span>Sign in required · {tool.processingTime}</span></div>
          </div>
        </div>
        <div className="workspace-auth-gate">
          <span className="upload-icon"><Coins size={26} /></span>
          <p className="eyebrow">Account required</p>
          <h2>Sign in to use {tool.name}.</h2>
          <p>Every new account receives 15 lifetime free credits once. Your balance is stored with your account, so refreshes, sign-outs and redeploys do not reset spent credits.</p>
          <div className="hero-actions">
            <button className="button button-primary" type="button" onClick={() => navigate(`/auth?mode=signup&next=${encodeURIComponent(next)}`)}>Create free account</button>
            <button className="button button-ghost" type="button" onClick={() => navigate(`/auth?next=${encodeURIComponent(next)}`)}>Sign in</button>
          </div>
          <small>Free: 15 lifetime credits · Paid plans refill according to the Pricing page.</small>
        </div>
      </section>
    );
  }

  return (
    <section className={cn('workspace', compact && 'workspace-compact')}>
      <div className="workspace-toolbar">
        <div className="workspace-title">
          <span className="tool-icon"><ToolIcon size={20} /></span>
          <div>
            <strong>{tool.name}</strong>
            <span>{tool.processingTime} · {file ? estimatedCredits : `from ${tool.creditCost}`} credits</span>
          </div>
        </div>
        {file && stage !== 'processing' && (
          <button className="text-button" type="button" onClick={reset}><RotateCcw size={16} /> Start over</button>
        )}
      </div>

      {stage === 'empty' && (
        <div className={cn('dropzone', dragging && 'dragging')} {...dropHandlers}>
          <div className="dropzone-grid" aria-hidden="true" />
          <div className="dropzone-orbit" aria-hidden="true"><span /><span /><span /></div>
          <div className="dropzone-scan" aria-hidden="true" />
          <div className="dropzone-spark dropzone-spark-one" aria-hidden="true" />
          <div className="dropzone-spark dropzone-spark-two" aria-hidden="true" />
          <div className="dropzone-spark dropzone-spark-three" aria-hidden="true" />
          <div className="upload-icon"><UploadCloud size={27} /><i aria-hidden="true" /></div>
          <p className="eyebrow">Upload or paste</p>
          <h2>Drop an image to {tool.shortName.toLowerCase()}</h2>
          <p>{tool.formats.join(', ')} · up to 50 MB</p>
          <button className="button button-primary" type="button" onClick={() => inputRef.current?.click()}>
            <ImagePlus size={18} /> Choose image
          </button>
          <div className="privacy-line">Your file is used only to create the requested result.</div>
          <input
            ref={inputRef}
            className="visually-hidden"
            type="file"
            accept={acceptedMimeTypes.join(',')}
            onChange={(event) => event.target.files?.[0] && void acceptFile(event.target.files[0])}
          />
        </div>
      )}

      {(stage === 'ready' || stage === 'error') && file && previewUrl && (
        <div className="workspace-ready">
          <div className="preview-panel">
            <img src={previewUrl} alt="Selected upload preview" />
            <button className="preview-remove" type="button" onClick={reset} aria-label="Remove image"><X size={17} /></button>
            <div className="file-chip">
              <FileImage size={16} />
              <span>{file.name}</span>
              <small>{formatBytes(file.size)}</small>
            </div>
          </div>
          <div className="settings-panel">
            <div>
              <p className="eyebrow">Selected workflow</p>
              <h3>{tool.name}</h3>
              <p>{tool.description}</p>
            </div>

            {tool.options.slice(0, 2).map((option) => (
              <OptionControl key={option.id} option={option} value={options[option.id]} plan={resolvedPlan} onChange={(value) => setOptions((current) => ({ ...current, [option.id]: value }))} />
            ))}

            {tool.options.length > 2 && (
              <div className="advanced-settings">
                <button className="advanced-toggle" type="button" onClick={() => setAdvancedOpen((value) => !value)}>
                  Advanced settings <ChevronDown className={advancedOpen ? 'open' : ''} size={17} />
                </button>
                {advancedOpen && tool.options.slice(2).map((option) => (
                  <OptionControl key={option.id} option={option} value={options[option.id]} plan={resolvedPlan} onChange={(value) => setOptions((current) => ({ ...current, [option.id]: value }))} />
                ))}
              </div>
            )}

            {file && hasScaleOption && !error && <div className="inline-notice">{tool.slug === 'enhance' ? (planAccess.maxScale === 8 ? 'Original-size enhancement is the default. Your plan also includes optional 2×, 4× and 8× output.' : `Original-size enhancement is the default. Your ${resolvedPlan === 'free' ? 'Free' : 'Starter'} plan also includes optional 2× and 4× output.`) : (planAccess.maxScale === 8 ? 'Your plan includes 2×, 4× and 8× output. Larger files use more credits based on their megapixels.' : `Your ${resolvedPlan === 'free' ? 'Free' : 'Starter'} plan includes output up to 4×. Creator and Studio include 8× output.`)}</div>}
            {error && <div className="inline-error">{error}</div>}
            <button className="button button-primary button-large process-button" type="button" onClick={run}>
              <Sparkles size={19} /> Start processing
              <span><Coins size={15} /> {estimatedCredits}</span>
            </button>
            {!session && <p className="settings-note">Create a free account to process this image. After sign-up, you will return to this workflow.</p>}
          </div>
        </div>
      )}

      {stage === 'processing' && previewUrl && (
        <div className="processing-stage">
          <div className="processing-preview"><img src={previewUrl} alt="Image being processed" /></div>
          <div className="processing-card">
            <div className="processing-ring"><LoaderCircle size={38} /></div>
            <p className="eyebrow">Nexhance AI processing</p>
            <h2>{progressLabel}</h2>
            <p>{processingHint} You can stay on this page while we finish the result for you.</p>
            <div className="progress-track"><span style={{ width: `${progress}%` }} /></div>
            <div className="progress-meta"><span>{progress}%</span><span>{processingHint}</span></div>
          </div>
        </div>
      )}

      {stage === 'complete' && result && previewUrl && (
        <div className="result-stage">
          <div className="result-header">
            <div>
              <span className="success-icon"><Check size={18} /></span>
              <div>
                <p className="eyebrow">Processing complete</p>
                <h2>Your image is ready</h2>
              </div>
            </div>
            <div className="result-actions">
              <button className="button button-ghost" type="button" onClick={reset}><RotateCcw size={17} /> New image</button>
            </div>
          </div>
          <div className="result-layout">
            <div className="result-preview-shell">
              <BeforeAfter
                before={previewUrl}
                after={result.outputUrl}
                alt="Processed image"
                className="result-compare"
                fit="contain"
                aspectRatio={compareAspectRatio}
              />
            </div>
            <aside className="result-side-panel">
              <div className="result-side-card primary">
                <strong>Download your result</strong>
                <p>Review the before-and-after preview, then save the processed image while it stays available.</p>
                <button className="button button-primary button-large" type="button" onClick={() => void downloadResult()} disabled={downloading}>
                  <Download size={18} /> {downloading ? 'Downloading…' : 'Download result'}
                </button>
                <button className="button button-ghost" type="button" onClick={() => window.open(result.outputUrl, '_blank', 'noopener,noreferrer')}>
                  View full image
                </button>
              </div>
              <div className="result-side-card">
                <div className="result-side-heading"><ArrowLeftRight size={16} /><span>Result details</span></div>
                <div className="result-stat-list">
                  <div><small>{tool.slug === 'enhance' ? 'Detail intensity' : 'Workflow'}</small><strong>{detailStrength !== null ? `${detailStrength}%` : tool.shortName}</strong></div>
                  <div><small>Selected output</small><strong>{selectedScaleLabel ?? 'Standard output'}</strong></div>
                  <div><small>Credits used</small><strong>{result.creditsUsed}</strong></div>
                  <div><small>Output size</small><strong>{result.width && result.height ? `${result.width} × ${result.height}` : 'Same as source'}</strong></div>
                  <div><small>Download type</small><strong>{outputExtension ?? 'Image file'}</strong></div>
                </div>
                <div className="result-meta-stack">
                  <span><ArrowLeftRight size={15} /> Drag the slider to compare</span>
                  <span>Available for 24 hours</span>
                </div>
              </div>
            </aside>
          </div>
          {downloadError && <div className="inline-error">{downloadError}</div>}
        </div>
      )}
    </section>
  );
}

function OptionControl({
  option,
  value,
  plan,
  onChange
}: {
  option: ToolDefinition['options'][number];
  value: string | number | boolean;
  plan: AccountPlan;
  onChange: (value: string | number | boolean) => void;
}) {
  return (
    <label className={cn('option-control', option.type === 'toggle' && 'toggle-control')}>
      <span className="option-label">
        <strong>{option.label}</strong>
        {option.help && <small>{option.help}</small>}
      </span>
      {option.type === 'select' && (
        <SelectMenu
          ariaLabel={option.label}
          value={String(value)}
          onChange={(next) => onChange(next)}
          choices={(option.choices ?? []).map((choice) => {
            const scaleLocked = option.id === 'scale' && Number(choice.value) > PLAN_ACCESS[plan].maxScale;
            const disabled = scaleLocked;
            return {
              value: String(choice.value),
              label: choice.label,
              disabled,
              note: scaleLocked ? 'Creator or Studio required' : undefined
            };
          })}
        />
      )}
      {option.type === 'toggle' && (
        <button
          type="button"
          className={cn('switch', value === true && 'on')}
          aria-pressed={value === true}
          onClick={() => onChange(value !== true)}
        ><span /></button>
      )}
      {option.type === 'range' && (
        <div className="range-wrap">
          <input
            type="range"
            min={option.min}
            max={option.max}
            step={option.step}
            value={Number(value)}
            onChange={(event) => onChange(Number(event.target.value))}
          />
          <span>{option.id === 'detailStrength' ? `${Math.round(Number(value))}%` : Number(value).toFixed(option.step && option.step < 1 ? 2 : 1)}</span>
        </div>
      )}
      {option.type === 'text' && (
        <textarea rows={3} value={String(value)} onChange={(event) => onChange(event.target.value)} />
      )}
    </label>
  );
}
